import { useState, useEffect } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useSession } from '@/lib/auth-client';
import { RiPlayLine, RiSaveLine, RiTimeLine, RiDatabase2Line, RiTableLine, RiArrowRightSLine, RiNodeTree, RiShareLine, RiMagicLine, RiSparklingLine } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { api, QueryResult, Database as DatabaseType } from '@/lib/api';
import { toast } from 'sonner';
import SchemaVisualizer from '@/components/schema-visualizer';
import { DatabaseSwitcher } from '@/components/database-switcher';
import { QueryBuilder } from '@/components/query-builder';
import { AIQueryAssistant } from '@/components/ai-query-assistant';
import { AIQueryTools } from '@/components/ai-query-tools';
import { AIErrorHelper } from '@/components/ai-error-helper';
import { SafetyConfirmationDialog } from '@/components/safety-confirmation-dialog';
import { ExportMenu } from '@/components/export-menu';
import { SandboxModeToggle } from '@/components/sandbox-mode-toggle';
import { SandboxExecutionFlow } from '@/components/sandbox-execution-flow';
import type { TableSchema } from '@/types/query-builder';
import { useDatabaseCollaboration } from '@/hooks/use-database-collaboration';
import { AvatarStack } from '@/components/avatar';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function QueryPageContent() {
  const { id } = useParams({ strict: false });
  const { data: session } = useSession();
  const { state: sidebarState, setOpen: setSidebarOpen } = useSidebar();
  const [query, setQuery] = useState('SELECT * FROM users LIMIT 10;');
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [tables, setTables] = useState<string[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [queryName, setQueryName] = useState('');
  const [queryDescription, setQueryDescription] = useState('');
  const [databaseInfo, setDatabaseInfo] = useState<DatabaseType | null>(null);
  const [activeTab, setActiveTab] = useState<'query' | 'builder' | 'schema'>('query');
  const [schema, setSchema] = useState<TableSchema[]>([]);
  const [showErrorHelper, setShowErrorHelper] = useState(false);
  const [queryError, setQueryError] = useState<string>('');
  const [showSafetyDialog, setShowSafetyDialog] = useState(false);
  const [pendingQuery, setPendingQuery] = useState<string>('');

  // Real-time collaboration
  const {
    activeUsers,
    isConnected: isCollabConnected,
    isLoading: isCollabLoading,
    error: collabError,
    totalUsers,
    broadcastQueryExecution,
    onQueryExecuted,
    broadcastCursorMove,
    activeCursors,
  } = useDatabaseCollaboration({
    databaseId: id,
    user: session?.user ? {
      id: session.user.id,
      name: session.user.name || 'Anonymous',
      email: session.user.email,
      image: session.user.image,
    } : null,
    enabled: !!id && !!session?.user,
  });

  // Show notifications for user join/leave (with debouncing to avoid spam)
  useEffect(() => {
    if (!session?.user || activeUsers.length === 0) return;

    // Filter out current user
    const otherUsers = activeUsers.filter(u => u.id !== session.user.id);

    // Only show notification if there are other users (not just yourself)
    if (otherUsers.length > 0) {
      const lastUser = otherUsers[otherUsers.length - 1];

      // Avoid showing notification on initial load
      const isInitialLoad = activeUsers.length === otherUsers.length + 1;
      if (!isInitialLoad && lastUser) {
        toast.info(`${lastUser.name} is viewing this database`, {
          duration: 3000,
        });
      }
    }
  }, [activeUsers.length, session?.user]);

  // Show error notification for collaboration errors
  useEffect(() => {
    if (collabError) {
      toast.error('Collaboration disconnected', {
        description: 'Real-time features may be unavailable',
        duration: 4000,
      });
    }
  }, [collabError]);

  // Listen for query executions from other users
  useEffect(() => {
    const unsubscribe = onQueryExecuted((execution) => {
      toast.info(`${execution.userName} executed query`, {
        description: `Completed in ${execution.executionTime}ms`,
        duration: 4000,
        action: {
          label: 'View',
          onClick: () => {
            // Show query in a simple alert for now (can enhance with modal later)
            alert(`Query executed by ${execution.userName}:\n\n${execution.query}`);
          },
        },
      });
    });

    return unsubscribe;
  }, [onQueryExecuted]);

  // Auto-collapse sidebar when switching to schema tab (but allow user to manually open it)
  useEffect(() => {
    if (activeTab === 'schema' && sidebarState === 'expanded') {
      setSidebarOpen(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (id && session?.user?.id) {
      loadDatabaseInfo();
      loadTables();
      loadSchema();
    }
  }, [id, session?.user?.id]);

  const loadDatabaseInfo = async () => {
    if (!session?.user?.id) return;

    try {
      const result = await api.listDatabases(session.user.id);
      const db = result.databases.find(d => d.id === id);
      if (db) {
        setDatabaseInfo(db);
      }
    } catch (error: any) {
      toast.error('Failed to load database info');
    }
  };

  const loadTables = async () => {
    try {
      const result = await api.getTables(id as string);
      setTables(result.tables);
    } catch (error: any) {
      toast.error('Failed to load tables');
    }
  };

  const loadSchema = async () => {
    try {
      const result = await api.getDatabaseSchema(id as string);
      setSchema(result.tables);
    } catch (error: any) {
      console.error('Failed to load schema', error);
      toast.error('Failed to load database schema');
    }
  };

  const checkQuerySafety = (sql: string): { safe: boolean; type?: 'DELETE' | 'DROP' | 'TRUNCATE' | 'ALTER' } => {
    const sqlUpper = sql.trim().toUpperCase();

    if (sqlUpper.startsWith('DROP')) {
      return { safe: false, type: 'DROP' };
    }
    if (sqlUpper.startsWith('DELETE')) {
      return { safe: false, type: 'DELETE' };
    }
    if (sqlUpper.startsWith('TRUNCATE')) {
      return { safe: false, type: 'TRUNCATE' };
    }
    if (sqlUpper.startsWith('ALTER')) {
      return { safe: false, type: 'ALTER' };
    }

    return { safe: true };
  };

  const executeQueryInternal = async (queryToExecute: string) => {
    if (!session?.user?.id || !id) return;

    setIsExecuting(true);
    try {
      const result = await api.executeQuery(id as string, queryToExecute);
      setResult(result);
      setQueryError('');
      toast.success(`Query executed in ${result.executionTime}ms`);

      broadcastQueryExecution(queryToExecute, result.executionTime);
    } catch (error: any) {
      setQueryError(error.message || 'Query failed');
      setShowErrorHelper(true);
    } finally {
      setIsExecuting(false);
    }
  };

  const executeQuery = async () => {
    if (!session?.user?.id || !id || !query.trim()) {
      toast.error('Please enter a query');
      return;
    }

    const safety = checkQuerySafety(query);
    if (!safety.safe && safety.type) {
      setPendingQuery(query);
      setShowSafetyDialog(true);
      return;
    }

    await executeQueryInternal(query);
  };

  const handleSafetyConfirm = async () => {
    await executeQueryInternal(pendingQuery);
    setPendingQuery('');
  };

  const saveQuery = async () => {
    if (!session?.user?.id || !id || !queryName.trim()) {
      toast.error('Please enter a query name');
      return;
    }

    try {
      await api.saveQuery({
        name: queryName,
        query,
        databaseId: id as string,
        userId: session.user.id,
        description: queryDescription,
      });
      toast.success('Query saved successfully');
      setShowSaveDialog(false);
      setQueryName('');
      setQueryDescription('');
    } catch (error: any) {
      toast.error('Failed to save query');
    }
  };

  const insertTableName = (tableName: string) => {
    setQuery((prev) => prev + (prev.trim() ? ' ' : '') + tableName);
  };

  return (
    <>
      {activeTab === 'query' && (
        <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 z-10">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/databases">Databases</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <RiArrowRightSLine className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {databaseInfo?.name || 'Query Editor'}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex items-center gap-3 ml-auto">
            <AIQueryTools
              databaseId={id as string}
              query={query}
              executionTime={result?.executionTime}
              onQueryUpdate={setQuery}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSaveDialog(true)}
              disabled={!query.trim()}
            >
              <RiSaveLine className="h-4 w-4 mr-2" />
              Save
            </Button>

            {/* Sandbox Mode Toggle */}
            <SandboxModeToggle databaseId={id as string} />

            {/* Smart Sandbox Execution Flow */}
            <SandboxExecutionFlow
              databaseId={id as string}
              query={query}
              onExecute={executeQueryInternal}
              disabled={!query.trim()}
            />

            {/* Real-time collaboration indicators */}
            {totalUsers > 0 && (
              <>
                <Separator orientation="vertical" className="h-6" />
                <TooltipProvider delayDuration={200}>
                  <div className="flex items-center gap-2">
                    {!isCollabConnected && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="size-2 rounded-full bg-amber-500 animate-pulse" />
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <p className="text-xs">Reconnecting...</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <span className="text-sm text-muted-foreground hidden lg:inline">
                      {totalUsers} {totalUsers === 1 ? 'viewer' : 'viewers'}
                    </span>
                    <AvatarStack animate size={28}>
                      {activeUsers.slice(0, 5).map((user) => (
                        <Tooltip key={user.id}>
                          <TooltipTrigger asChild>
                            <Avatar className="cursor-pointer transition-transform hover:scale-110 border-2 border-background">
                              <AvatarImage
                                src={user.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
                              />
                              <AvatarFallback className="text-xs">
                                {user.name.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            <p className="text-xs font-medium">{user.name}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                      {totalUsers > 5 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center justify-center size-7 rounded-full bg-muted border-2 border-background text-xs font-medium">
                              +{totalUsers - 5}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            <p className="text-xs">{totalUsers - 5} more viewer{totalUsers - 5 > 1 ? 's' : ''}</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </AvatarStack>
                  </div>
                </TooltipProvider>
              </>
            )}

            {session?.user && (
              <div className="size-8">
                <img
                  src={session.user.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${session.user.email}`}
                  alt={session.user.name || 'User'}
                  className="rounded-full ring-2 ring-background"
                />
              </div>
            )}
            <ModeToggle />
          </div>
        </header>
      )}

        <main className="flex-1 flex flex-col overflow-hidden">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'query' | 'builder' | 'schema')} className="flex-1 flex flex-col">
            <div className="border-b px-4 relative z-[100]">
              <TabsList>
                <TabsTrigger value="query" className="flex items-center gap-2">
                  <RiPlayLine className="h-4 w-4" />
                  SQL Editor
                </TabsTrigger>
                <TabsTrigger value="builder" className="flex items-center gap-2">
                  <RiMagicLine className="h-4 w-4" />
                  Query Builder
                </TabsTrigger>
                <TabsTrigger value="schema" className="flex items-center gap-2">
                  <RiNodeTree className="h-4 w-4" />
                  Schema
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="query" className="flex-1 flex flex-col lg:flex-row overflow-hidden m-0">
          {/* Tables Sidebar */}
          <div className="lg:w-64 border-b lg:border-b-0 lg:border-r bg-muted/30">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <RiTableLine className="h-4 w-4" />
                  Tables
                </h3>
                <Badge variant="outline" className="text-xs">
                  {tables.length}
                </Badge>
              </div>
              <ScrollArea className="h-[calc(100vh-8rem)]">
                <div className="space-y-1">
                  {tables.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tables found</p>
                  ) : (
                    tables.map((table) => (
                      <Button
                        key={table}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start font-mono text-xs hover:bg-primary/10"
                        onClick={() => insertTableName(table)}
                      >
                        <RiTableLine className="h-3 w-3 mr-2 text-muted-foreground" />
                        {table}
                      </Button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col">
            {/* Editor Header */}
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-medium text-muted-foreground">SQL Editor</h3>
            </div>

            {/* AI Assistant Section */}
            <div className="px-4 pt-4">
              <AIQueryAssistant
                databaseId={id as string}
                onSQLGenerated={(sql) => {
                  setResult(null);
                  setQuery(sql);
                  setTimeout(() => executeQuery(), 100);
                }}
                autoExecute={true}
              />
            </div>

            {/* SQL Editor with Actions */}
            <div className="p-4 space-y-3 border-b">
              <Textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Enter your SQL query here..."
                className="font-mono min-h-[150px] resize-none text-sm"
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    executeQuery();
                  }
                }}
              />

              {/* Action Bar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    onClick={executeQuery}
                    disabled={isExecuting || !query.trim()}
                    size="sm"
                    className="gap-2"
                  >
                    <RiPlayLine className="h-4 w-4" />
                    {isExecuting ? 'Running...' : 'Run Query'}
                  </Button>
                  <Button
                    onClick={() => setShowSaveDialog(true)}
                    disabled={!query.trim()}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <RiSaveLine className="h-4 w-4" />
                    Save
                  </Button>
                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>Press Cmd/Ctrl + Enter to run</span>
                  {query.trim() && (
                    <>
                      <span>•</span>
                      <span>{query.split('\n').length} lines</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {result ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <Badge variant="outline">
                        {result.rowCount} {result.rowCount === 1 ? 'row' : 'rows'}
                      </Badge>
                      <Badge variant="outline" className="flex items-center gap-1">
                        <RiTimeLine className="h-3 w-3" />
                        {result.executionTime}ms
                      </Badge>
                    </div>
                    <ExportMenu result={result} query={query} />
                  </div>

                  {result.data.length === 0 ? (
                    <Card>
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <p className="text-muted-foreground">No results found</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="overflow-hidden">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {Object.keys(result.data[0]).map((key) => (
                                <TableHead key={key} className="font-semibold whitespace-nowrap">
                                  {key}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {result.data.map((row, idx) => (
                              <TableRow key={idx}>
                                {Object.values(row).map((value: any, colIdx) => (
                                  <TableCell
                                    key={colIdx}
                                    className="font-mono text-xs max-w-[300px]"
                                  >
                                    <div className="overflow-x-auto overflow-y-hidden whitespace-nowrap scrollbar-thin">
                                      <div className="inline-block max-w-full">
                                        {value === null
                                          ? <span className="text-muted-foreground italic">NULL</span>
                                          : typeof value === 'object'
                                          ? JSON.stringify(value)
                                          : String(value)}
                                      </div>
                                    </div>
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </Card>
                  )}
                </motion.div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <RiPlayLine className="h-12 w-12 mx-auto mb-4 text-muted-foreground/20" />
                    <p className="text-muted-foreground">
                      Run a query to see results
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
            </TabsContent>

            <TabsContent value="builder" className="flex-1 overflow-auto p-6 m-0">
              <div className="max-w-5xl mx-auto space-y-6">
                {/* Query Builder Introduction */}
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-primary/10 p-2">
                      <RiMagicLine className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm mb-1">Visual Query Builder</h3>
                      <p className="text-sm text-muted-foreground">
                        Build SQL queries visually without writing code. Select tables, choose columns, add filters, and joins -
                        the SQL is generated automatically. Perfect for beginners or quick data exploration.
                      </p>
                      <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Drag & Drop Interface
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          Live SQL Preview
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                          No SQL Knowledge Required
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <QueryBuilder
                  schema={schema}
                  onExecute={(sql) => {
                    setQuery(sql);
                    setActiveTab('query');
                    setTimeout(() => executeQuery(), 100);
                  }}
                />
              </div>
            </TabsContent>

            <TabsContent value="schema" className="absolute inset-0 top-0 m-0">
              {/* Floating Header for Schema View */}
              <header className="absolute top-2 md:top-3 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1rem)] md:w-[calc(100%-1.5rem)] max-w-6xl">
                <div className="border border-border/80 rounded-xl bg-card/95 backdrop-blur-xl h-14 flex justify-between items-center gap-2 px-5 shadow-lg">
                  {/* Left area */}
                  <div className="flex-1 flex items-center gap-2">
                    <SidebarTrigger className="-ml-1" />
                    <Separator orientation="vertical" className="h-4 mr-1" />
                    <Link to="/dashboard" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity">
                      {databaseInfo?.type === 'postgres' && (
                        <svg className="size-6 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.128 0c-.881 0-1.686.088-2.417.24-.731.152-1.369.36-1.905.624-.536.264-.958.568-1.265.912a2.06 2.06 0 0 0-.456 1.296c0 .352.104.656.312.912.208.256.488.456.84.6.352.144.744.24 1.176.288.432.048.872.072 1.32.072.496 0 .968-.032 1.416-.096.448-.064.84-.168 1.176-.312.336-.144.6-.328.792-.552.192-.224.288-.496.288-.816 0-.304-.096-.568-.288-.792a2.145 2.145 0 0 0-.768-.576 4.627 4.627 0 0 0-1.104-.336 7.558 7.558 0 0 0-1.296-.12c-.368 0-.728.032-1.08.096a4.648 4.648 0 0 0-.984.288 2.61 2.61 0 0 0-.768.48c-.208.192-.312.416-.312.672 0 .24.088.448.264.624.176.176.408.32.696.432.288.112.616.192.984.24.368.048.744.072 1.128.072h.24c.096 0 .184-.008.264-.024v.888c-.096.016-.192.024-.288.024h-.288c-.496 0-.968-.04-1.416-.12a5.906 5.906 0 0 1-1.248-.36 2.945 2.945 0 0 1-.936-.648c-.24-.272-.36-.6-.36-.984 0-.4.12-.752.36-1.056.24-.304.56-.56.96-.768.4-.208.856-.368 1.368-.48a7.93 7.93 0 0 1 1.632-.168c.528 0 1.032.048 1.512.144.48.096.904.24 1.272.432.368.192.664.432.888.72.224.288.336.624.336 1.008 0 .4-.112.752-.336 1.056-.224.304-.52.56-.888.768-.368.208-.792.368-1.272.48-.48.112-.984.168-1.512.168-.432 0-.856-.032-1.272-.096a5.906 5.906 0 0 1-1.176-.312 3.144 3.144 0 0 1-.912-.552 2.46 2.46 0 0 1-.6-.792 2.228 2.228 0 0 1-.216-.984c0-.448.112-.848.336-1.2.224-.352.52-.656.888-.912.368-.256.792-.456 1.272-.6.48-.144.984-.216 1.512-.216.544 0 1.064.064 1.56.192.496.128.936.312 1.32.552.384.24.688.536.912.888.224.352.336.752.336 1.2z"/>
                        </svg>
                      )}
                      {databaseInfo?.type === 'mysql' && (
                        <svg className="size-6 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16.405 5.501c-.115 0-.193.014-.274.033v.013h.014c.054.104.146.18.214.273.054.107.1.214.154.32l.014-.015c.094-.066.14-.172.14-.333-.04-.047-.046-.094-.08-.14-.04-.067-.126-.1-.18-.153zM5.77 18.695h-.927a50.854 50.854 0 00-.27-4.41h-.008l-1.41 4.41H2.45l-1.4-4.41h-.01a72.892 72.892 0 00-.195 4.41H0c.055-1.966.192-3.81.41-5.53h1.15l1.335 4.064h.008l1.347-4.064h1.095c.242 2.015.384 3.86.428 5.53zm4.017-4.08c-.378 2.045-.876 3.533-1.492 4.46-.482.716-1.01 1.073-1.583 1.073-.153 0-.34-.046-.566-.138v-.494c.11.017.24.026.386.026.268 0 .483-.075.647-.222.197-.18.295-.382.295-.605 0-.155-.077-.47-.23-.944L6.23 14.615h.91l.727 2.36c.164.536.233.91.205 1.123.4-1.064.678-2.227.835-3.483zm12.325 4.08h-2.63v-5.53h.885v4.85h1.745zm-3.32.135l-1.016-.5c.09-.076.177-.158.255-.25.433-.506.648-1.258.648-2.253 0-1.83-.718-2.746-2.155-2.746-.704 0-1.254.232-1.65.697-.43.508-.646 1.256-.646 2.245 0 .972.19 1.686.574 2.14.35.41.877.615 1.583.615.264 0 .506-.033.725-.098l1.325.772.36-.622zM15.5 17.588c-.225-.36-.337-.94-.337-1.736 0-1.393.424-2.09 1.27-2.09.443 0 .77.167.977.5.224.362.336.936.336 1.723 0 1.404-.424 2.108-1.27 2.108-.445 0-.77-.167-.978-.5zm-1.658-.425c0 .47-.172.856-.516 1.156-.344.3-.803.45-1.384.45-.543 0-1.064-.172-1.573-.515l.237-.476c.438.22.833.328 1.19.328.332 0 .593-.073.783-.22a.754.754 0 00.3-.615c0-.33-.23-.61-.648-.845-.388-.213-1.163-.657-1.163-.657-.422-.307-.632-.636-.632-1.177 0-.45.157-.81.47-1.085.315-.278.72-.415 1.22-.415.512 0 .98.136 1.4.41l-.213.476a2.726 2.726 0 00-1.064-.23c-.283 0-.502.068-.654.206a.685.685 0 00-.248.524c0 .328.234.61.666.85.393.215 1.187.67 1.187.67.433.305.648.63.648 1.168z"/>
                        </svg>
                      )}
                      {databaseInfo?.type === 'sqlite' && (
                        <svg className="size-6 text-foreground" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M21.678 16.047c-.33 0-.596.267-.596.597v.896c0 .33-.267.596-.596.596H3.514c-.33 0-.596-.267-.596-.596v-.896c0-.33-.267-.597-.596-.597s-.597.267-.597.597v.896c0 .99.805 1.79 1.79 1.79h16.97c.986 0 1.79-.8 1.79-1.79v-.896c0-.33-.267-.597-.596-.597zM3.514 10.433h16.97c.33 0 .597-.268.597-.597V8.94c0-.33-.268-.597-.596-.597s-.597.268-.597.597v.298H3.514v-.298c0-.33-.268-.597-.597-.597s-.597.268-.597.597v.896c0 .33.268.597.597.597zm0-5.37h16.97c.33 0 .597-.267.597-.596s-.268-.597-.596-.597H3.514c-.33 0-.597.268-.597.597s.268.597.597.597z"/>
                        </svg>
                      )}
                      {!databaseInfo?.type && <RiDatabase2Line className="size-6 text-foreground" />}
                    </Link>
                  </div>
                  {/* Center area */}
                  <div className="grow flex justify-center">
                    {id && <DatabaseSwitcher currentDatabaseId={id as string} />}
                  </div>
                  {/* Right area */}
                  <div className="flex-1 flex justify-end items-center gap-3">
                    <Button size="sm" variant="ghost" className="text-sm rounded-lg h-9 max-sm:hidden">
                      <RiShareLine className="size-4 mr-2" />
                      Share
                    </Button>
                    {session?.user && (
                      <div className="size-8">
                        <img
                          src={session.user.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${session.user.email}`}
                          alt={session.user.name || 'User'}
                          className="rounded-full ring-2 ring-background"
                        />
                      </div>
                    )}
                    <ModeToggle />
                  </div>
                </div>
              </header>

              {id && (
                <SchemaVisualizer
                  databaseId={id as string}
                  broadcastCursorMove={broadcastCursorMove}
                  activeCursors={activeCursors}
                />
              )}
            </TabsContent>
          </Tabs>
        </main>

      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Query</DialogTitle>
            <DialogDescription>
              Save this query for quick access later
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="queryName">Query Name</Label>
              <Input
                id="queryName"
                placeholder="My awesome query"
                value={queryName}
                onChange={(e) => setQueryName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="queryDescription">Description (optional)</Label>
              <Textarea
                id="queryDescription"
                placeholder="What does this query do?"
                value={queryDescription}
                onChange={(e) => setQueryDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveQuery} disabled={!queryName.trim()}>
              Save Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AIErrorHelper
        databaseId={id as string}
        query={query}
        error={queryError}
        open={showErrorHelper}
        onOpenChange={setShowErrorHelper}
        onApplyFix={(fixedQuery) => {
          setQuery(fixedQuery);
          setShowErrorHelper(false);
        }}
      />

      <SafetyConfirmationDialog
        open={showSafetyDialog}
        onOpenChange={setShowSafetyDialog}
        query={pendingQuery}
        operationType={checkQuerySafety(pendingQuery).type || 'DELETE'}
        onConfirm={handleSafetyConfirm}
      />
    </>
  );
}

export function QueryPage() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <QueryPageContent />
      </SidebarInset>
    </SidebarProvider>
  );
}
